import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Layout from '@/components/Layout';
import { useToast } from "@/hooks/use-toast";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Heart, ChevronUp, ChevronDown, Award, Trophy, 
  Clock, CheckCircle2, Calendar, Badge as BadgeIcon, 
  MenuSquare, Compass, Medal, Star, Target, Sun, Droplet, Activity, Moon, Zap, Users, MessageSquare, Bookmark
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { triggerLisaChat, triggerPointsUpdated } from '@/utils/onboardingEvents';
import { useRecipes } from '@/contexts/RecipesContext';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, addDays, isAfter, differenceInDays, isSameDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import UserLevel from '@/components/UserLevel';

interface Challenge {
  id: number;
  title: string;
  description: string;
  duration: string;
  difficulty: string;
  icon: string;
  completed: boolean;
  type?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  progress?: number;
  objectives?: {
    menuRecipesCount: number;
    targetRecipes: number;
    objectivesChanged: boolean;
    dailyChanges?: string[]; 
  } | {
    hydrationDays: string[];
    restEnergyDays: string[];
    activityDays: string[];
    sunDays: string[];
    requiredDays: number;
  } | {
    likes: number;
    comments: number;
    favorites: number;
    requiredCount: number;
  };
  points?: number;
}

const explorerChallenge: Challenge = {
  id: 5,
  title: "Explorador Vegano",
  description: "Añade 30 recetas diferentes a tu menú en un periodo de 30 días y realiza al menos un cambio en tu diario nutricional cada día durante 30 días.",
  duration: "30 días",
  difficulty: "Intermedio",
  icon: "Compass",
  type: "special",
  completed: false,
  startDate: null,
  endDate: null,
  progress: 0,
  objectives: {
    menuRecipesCount: 0,
    targetRecipes: 30,
    objectivesChanged: false,
    dailyChanges: []
  },
  points: 200
};

const zenVegChallenge: Challenge = {
  id: 6,
  title: "Zen Veg🧘‍♀️",
  description: "Durante 7 días seguidos registra tu hidratación, nivel de descanso y energía, actividad física y el sol que has tomado.",
  duration: "7 días",
  difficulty: "Principiante",
  icon: "Sun",
  type: "weekly",
  completed: false,
  startDate: null,
  endDate: null,
  progress: 0,
  objectives: {
    hydrationDays: [],
    restEnergyDays: [],
    activityDays: [],
    sunDays: [],
    requiredDays: 7
  },
  points: 30
};

const socialChallenge: Challenge = {
  id: 7,
  title: "5 Social",
  description: "Da 5 likes a recetas, escribe 5 comentarios y guarda 5 recetas en favoritos.",
  duration: "Sin límite",
  difficulty: "Principiante",
  icon: "Users",
  type: "social",
  completed: false,
  startDate: null,
  endDate: null,
  progress: 0,
  objectives: {
    likes: 0,
    comments: 0,
    favorites: 0,
    requiredCount: 5
  },
  points: 100
};

const levelData = [
  {
    level: 1,
    name: "Semilla consciente 🌱",
    requiredPoints: 0,
    description: "Empiezas a brotar! Todo gran cambio nace con una intención."
  },
  {
    level: 2,
    name: "Brotecito constante 🌿",
    requiredPoints: 100,
    description: "Tu energía crece día a día. ¡Sigue regándote con buenos hábitos!"
  },
  {
    level: 3,
    name: "Raíces fuertes 🌾",
    requiredPoints: 300,
    description: "Tu rutina se afianza. Estás echando raíces de bienestar."
  },
  {
    level: 4,
    name: "Cocinillas vital 🥑",
    requiredPoints: 600,
    description: "Tu cocina es tu poder. Lo que comes, te transforma."
  },
  {
    level: 5,
    name: "Aliado verde 💚",
    requiredPoints: 1000,
    description: "Ya no solo lo haces por ti. Inspiras a otros con tu camino."
  },
  {
    level: 6,
    name: "Embajador/a vegano/a 🕊️",
    requiredPoints: 1600,
    description: "Tu energía, tu coherencia y tu constancia hablan por ti."
  },
  {
    level: 7,
    name: "Maestr@ del bienestar ✨",
    requiredPoints: 2300,
    description: "Vives en equilibrio, y eso se nota. Este estilo ya es tuyo."
  },
  {
    level: 8,
    name: "Leyenda Vital 🌍",
    requiredPoints: 3000,
    description: "Tu recorrido es inspiración. ¡Sigue brillando para ti y para el mundo!"
  }
];

const Challenges = () => {
  const { toast } = useToast();
  const [isLisaBoxOpen, setIsLisaBoxOpen] = useState(true);
  const [isLevelTableOpen, setIsLevelTableOpen] = useState(true);
  const [explorer, setExplorer] = useState<Challenge>(explorerChallenge);
  const [zenVeg, setZenVeg] = useState<Challenge>(zenVegChallenge);
  const [social, setSocial] = useState<Challenge>(socialChallenge);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [zenCompletionDialogOpen, setZenCompletionDialogOpen] = useState(false);
  const [socialCompletionDialogOpen, setSocialCompletionDialogOpen] = useState(false);
  const [activeChallenge, setActiveChallenge] = useState<Challenge | null>(null);
  const [showCompletedChallenges, setShowCompletedChallenges] = useState(false);
  const [userLevelRefreshKey, setUserLevelRefreshKey] = useState(0);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { recipes } = useRecipes();

  const handleLisaAvatarClick = () => {
    triggerLisaChat();
  };

  const startExplorerChallenge = () => {
    const today = new Date();
    const thirtyDaysLater = addDays(today, 30);
    
    const updatedExplorer = {
      ...explorer,
      startDate: today,
      endDate: thirtyDaysLater,
      progress: 0,
      objectives: {
        ...explorer.objectives,
        menuRecipesCount: 0,
        objectivesChanged: false,
        dailyChanges: []
      }
    };
    
    setExplorer(updatedExplorer);
    localStorage.setItem(`challenge_explorer_${user?.id}`, JSON.stringify(updatedExplorer));
    sessionStorage.setItem(`challenge_explorer_${user?.id}`, JSON.stringify(updatedExplorer));
    
    localStorage.removeItem(`nutrition_journal_changed_${user?.id}`);
    
    toast({
      title: "¡Reto iniciado!",
      description: "Tienes 30 días para completar el Reto Explorador",
      notificationType: "user"
    });
  };

  const startZenVegChallenge = () => {
    const today = new Date();
    const sevenDaysLater = addDays(today, 7);
    
    const updatedZenVeg = {
      ...zenVeg,
      startDate: today,
      endDate: sevenDaysLater,
      progress: 0,
      objectives: {
        hydrationDays: [],
        restEnergyDays: [],
        activityDays: [],
        sunDays: [],
        requiredDays: 7
      }
    };
    
    setZenVeg(updatedZenVeg);
    localStorage.setItem(`challenge_zenVeg_${user?.id}`, JSON.stringify(updatedZenVeg));
    sessionStorage.setItem(`challenge_zenVeg_${user?.id}`, JSON.stringify(updatedZenVeg));
    
    toast({
      title: "¡Reto iniciado!",
      description: "Tienes 7 días para completar el Reto Zen Veg",
      notificationType: "user"
    });
  };

  const startSocialChallenge = () => {
    const today = new Date();
    
    const updatedSocial = {
      ...social,
      startDate: today,
      endDate: null,
      progress: 0,
      objectives: {
        likes: 0,
        comments: 0,
        favorites: 0,
        requiredCount: 5
      }
    };
    
    setSocial(updatedSocial);
    localStorage.setItem(`challenge_social_${user?.id}`, JSON.stringify(updatedSocial));
    sessionStorage.setItem(`challenge_social_${user?.id}`, JSON.stringify(updatedSocial));
    
    localStorage.setItem(`challenge_social_${user?.id}_active`, 'true');
    
    toast({
      title: "¡Reto iniciado!",
      description: "Completa interacciones sociales para superar el reto",
      notificationType: "user"
    });
  };

  const updateUserPoints = async (points: number) => {
    if (!user?.id) return;
    
    try {
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('points')
        .eq('id', user.id)
        .single();
      
      if (fetchError) {
        console.error("Error fetching user profile:", fetchError);
        return;
      }
      
      const currentPoints = profile?.points || 0;
      const newPoints = currentPoints + points;
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ points: newPoints })
        .eq('id', user.id);
        
      if (updateError) {
        console.error("Error updating user points:", updateError);
      } else {
        console.log(`Updated user points: ${currentPoints} → ${newPoints}`);
        
        triggerPointsUpdated(user.id, newPoints);
        
        setUserLevelRefreshKey(prev => prev + 1);
        
        toast({
          title: "¡Puntos añadidos!",
          description: `Has ganado ${points} puntos.`,
          notificationType: "user"
        });
      }
    } catch (err) {
      console.error("Error in points update:", err);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const socialCompletionShown = localStorage.getItem(`challenge_social_${user.id}_completion_shown`) === 'true';

    const savedSocial = localStorage.getItem(`challenge_social_${user.id}`);
    const isSocialActive = localStorage.getItem(`challenge_social_${user.id}_active`) === 'true';
                        
    if (savedSocial && isSocialActive) {
      try {
        const parsedSocial = JSON.parse(savedSocial);
        
        if (parsedSocial.startDate) {
          parsedSocial.startDate = new Date(parsedSocial.startDate);
        }
        
        console.log('Loading saved social challenge:', parsedSocial);
        setSocial(parsedSocial);
        
        if (parsedSocial.completed && !socialCompletionShown) {
          setActiveChallenge(parsedSocial);
          setSocialCompletionDialogOpen(true);
          localStorage.setItem(`challenge_social_${user.id}_completion_shown`, 'true');
        }
      } catch (error) {
        console.error('Error parsing social challenge data:', error);
        localStorage.removeItem(`challenge_social_${user.id}_active`);
      }
    }
    
    const explorerCompletionShown = localStorage.getItem(`challenge_explorer_${user.id}_completion_shown`) === 'true';
    
    const savedExplorer = sessionStorage.getItem(`challenge_explorer_${user.id}`) || 
                          localStorage.getItem(`challenge_explorer_${user.id}`);
                          
    if (savedExplorer) {
      const parsedExplorer = JSON.parse(savedExplorer);
      if (parsedExplorer.startDate) {
        parsedExplorer.startDate = new Date(parsedExplorer.startDate);
      }
      if (parsedExplorer.endDate) {
        parsedExplorer.endDate = new Date(parsedExplorer.endDate);
      }
      
      setExplorer(parsedExplorer);
      
      if (parsedExplorer.completed && !explorerCompletionShown) {
        setActiveChallenge(parsedExplorer);
        setCompletionDialogOpen(true);
        localStorage.setItem(`challenge_explorer_${user.id}_completion_shown`, 'true');
      }
    }
    
    const zenVegCompletionShown = localStorage.getItem(`challenge_zenVeg_${user.id}_completion_shown`) === 'true';
    
    const savedZenVeg = sessionStorage.getItem(`challenge_zenVeg_${user.id}`) || 
                        localStorage.getItem(`challenge_zenVeg_${user.id}`);
                        
    if (savedZenVeg) {
      const parsedZenVeg = JSON.parse(savedZenVeg);
      if (parsedZenVeg.startDate) {
        parsedZenVeg.startDate = new Date(parsedZenVeg.startDate);
      }
      if (parsedZenVeg.endDate) {
        parsedZenVeg.endDate = new Date(parsedZenVeg.endDate);
      }
      
      setZenVeg(parsedZenVeg);
      
      if (parsedZenVeg.completed && !zenVegCompletionShown) {
        setActiveChallenge(parsedZenVeg);
        setZenCompletionDialogOpen(true);
        localStorage.setItem(`challenge_zenVeg_${user.id}_completion_shown`, 'true');
      }
    }

    const handleSocialChallengeUpdate = () => {
      const updatedSocial = localStorage.getItem(`challenge_social_${user.id}`);
      if (updatedSocial) {
        try {
          const parsedSocial = JSON.parse(updatedSocial);
          if (parsedSocial.startDate) {
            parsedSocial.startDate = new Date(parsedSocial.startDate);
          }
          
          setSocial(parsedSocial);
          
          if (parsedSocial.completed && !localStorage.getItem(`challenge_social_${user.id}_completion_shown`)) {
            setActiveChallenge(parsedSocial);
            setSocialCompletionDialogOpen(true);
            localStorage.setItem(`challenge_social_${user.id}_completion_shown`, 'true');
            
            if (!localStorage.getItem(`challenge_social_${user.id}_awarded`)) {
              updateUserPoints(parsedSocial.points || 100);
              localStorage.setItem(`challenge_social_${user.id}_awarded`, 'true');
            }
          }
        } catch (error) {
          console.error('Error handling social challenge update:', error);
        }
      }
    };
    
    window.addEventListener('like-added', handleSocialChallengeUpdate);
    window.addEventListener('comment-added', handleSocialChallengeUpdate);
    window.addEventListener('favorite-added', handleSocialChallengeUpdate);
    
    if (explorer.startDate && explorer.endDate && isAfter(new Date(), explorer.endDate)) {
      const updatedExplorer = { ...explorer, startDate: null, endDate: null };
      setExplorer(updatedExplorer);
      localStorage.setItem(`challenge_explorer_${user.id}`, JSON.stringify(updatedExplorer));
      sessionStorage.setItem(`challenge_explorer_${user.id}`, JSON.stringify(updatedExplorer));
      
      toast({
        title: "Reto Explorador finalizado",
        description: "El tiempo ha expirado. ¡Puedes intentarlo de nuevo!",
        notificationType: "user"
      });
    }
    
    if (zenVeg.startDate && zenVeg.endDate && isAfter(new Date(), zenVeg.endDate)) {
      const updatedZenVeg = { ...zenVeg, startDate: null, endDate: null };
      setZenVeg(updatedZenVeg);
      localStorage.setItem(`challenge_zenVeg_${user.id}`, JSON.stringify(updatedZenVeg));
      sessionStorage.setItem(`challenge_zenVeg_${user.id}`, JSON.stringify(updatedZenVeg));
      
      toast({
        title: "Reto Zen Veg finalizado",
        description: "El tiempo ha expirado. ¡Puedes intentarlo de nuevo!",
        notificationType: "user"
      });
    }
    
    return () => {
      window.removeEventListener('like-added', handleSocialChallengeUpdate);
      window.removeEventListener('comment-added', handleSocialChallengeUpdate);
      window.removeEventListener('favorite-added', handleSocialChallengeUpdate);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (explorer.completed) return;
    
    if (explorer.startDate && explorer.endDate) {
      let currentProgress = 0;
      let recipeCount = 0;
      
      const explorerObjectives = explorer.objectives as {
        menuRecipesCount: number;
        targetRecipes: number;
        objectivesChanged: boolean;
        dailyChanges?: string[];
      };
      
      let objectivesChanged = explorerObjectives.objectivesChanged || false;
      let dailyChanges = explorerObjectives.dailyChanges || [];
      
      const nutritionJournalChanged = localStorage.getItem(`nutrition_journal_changed_${user.id}`);
      const today = new Date();
      
      if (nutritionJournalChanged === 'true') {
        const todayString = today.toISOString().split('T')[0];
        if (!dailyChanges.includes(todayString)) {
          dailyChanges.push(todayString);
        }
        
        objectivesChanged = dailyChanges.length > 0;
      }
      
      try {
        const weeklyMenuKey = Object.keys(localStorage).find(key => 
          key.startsWith(`menu_${user.id}`) && localStorage.getItem(key));
            
        if (weeklyMenuKey) {
          const weeklyMenuData = JSON.parse(localStorage.getItem(weeklyMenuKey) || '{}');
          if (weeklyMenuData.days) {
            const addedRecipes = new Set();
            
            Object.values(weeklyMenuData.days).forEach((day: any) => {
              Object.values(day).forEach((mealType: any) => {
                if (Array.isArray(mealType)) {
                  mealType.forEach((meal: any) => {
                    if (meal.recipeId) {
                      addedRecipes.add(meal.recipeId);
                    }
                  });
                }
              });
            });
            
            recipeCount = addedRecipes.size;
            console.log("Recipe count from localStorage:", recipeCount);
          }
        }
      } catch (err) {
        console.error("Error reading weekly menu from localStorage:", err);
      }
      
      const recipeProgress = Math.min(recipeCount / 30, 1) * 0.5;
      const daysProgress = Math.min(dailyChanges.length / 30, 1) * 0.5;
      
      currentProgress = (recipeProgress + daysProgress) * 100;
      
      const updatedExplorer = {
        ...explorer,
        progress: currentProgress,
        objectives: {
          menuRecipesCount: recipeCount,
          targetRecipes: 30,
          objectivesChanged: objectivesChanged,
          dailyChanges: dailyChanges
        }
      };
      
      if (recipeCount >= 30 && 
          dailyChanges.length >= 30 && 
          !explorer.completed) {
        
        updatedExplorer.completed = true;
        
        localStorage.setItem(`badge_explorer_${user.id}`, 'true');
        sessionStorage.setItem(`badge_explorer_${user.id}`, 'true');
        
        updateUserPoints(explorer.points || 50);
        
        setActiveChallenge(updatedExplorer);
        setCompletionDialogOpen(true);
      }
      
      setExplorer(updatedExplorer);
      localStorage.setItem(`challenge_explorer_${user.id}`, JSON.stringify(updatedExplorer));
      sessionStorage.setItem(`challenge_explorer_${user.id}`, JSON.stringify(updatedExplorer));
    }
  }, [user, explorer.startDate, recipes]);

  useEffect(() => {
    if (!user?.id || !zenVeg.startDate || zenVeg.completed) return;
    
    const hydrationTracker = localStorage.getItem(`zenVeg_hydration_${user.id}`) ? 
      JSON.parse(localStorage.getItem(`zenVeg_hydration_${user.id}`) || '[]') : [];
      
    const restEnergyTracker = localStorage.getItem(`zenVeg_restEnergy_${user.id}`) ? 
      JSON.parse(localStorage.getItem(`zenVeg_restEnergy_${user.id}`) || '[]') : [];
      
    const activityTracker = localStorage.getItem(`zenVeg_activity_${user.id}`) ? 
      JSON.parse(localStorage.getItem(`zenVeg_activity_${user.id}`) || '[]') : [];
      
    const sunTracker = localStorage.getItem(`zenVeg_sun_${user.id}`) ? 
      JSON.parse(localStorage.getItem(`zenVeg_sun_${user.id}`) || '[]') : [];
    
    const today = new Date().toISOString().split('T')[0];
    const journalKey = `journal_${user.id}_${today}`;
    const todayJournal = localStorage.getItem(journalKey);
    
    if (todayJournal) {
      const journal = JSON.parse(todayJournal);
      
      if (journal.waterIntake > 0 && !hydrationTracker.includes(today)) {
        hydrationTracker.push(today);
        localStorage.setItem(`zenVeg_hydration_${user.id}`, JSON.stringify(hydrationTracker));
      }
      
      if ((journal.restLevel >= 5 || journal.energyLevel >= 5) && !restEnergyTracker.includes(today)) {
        restEnergyTracker.push(today);
        localStorage.setItem(`zenVeg_restEnergy_${user.id}`, JSON.stringify(restEnergyTracker));
      }
      
      if ((journal.steps > 0 || journal.workout.minutes > 0) && !activityTracker.includes(today)) {
        activityTracker.push(today);
        localStorage.setItem(`zenVeg_activity_${user.id}`, JSON.stringify(activityTracker));
      }
      
      if (journal.sunExposure > 0 && !sunTracker.includes(today)) {
        sunTracker.push(today);
        localStorage.setItem(`zenVeg_sun_${user.id}`, JSON.stringify(sunTracker));
      }
    }
    
    const uniqueDays = new Set([
      ...hydrationTracker,
      ...restEnergyTracker,
      ...activityTracker,
      ...sunTracker
    ]);
    
    const completeDays = [...uniqueDays].filter(day => 
      hydrationTracker.includes(day) && 
      restEnergyTracker.includes(day) && 
      activityTracker.includes(day) && 
      sunTracker.includes(day)
    );
    
    const requiredDays = 7;
    const progress = Math.min((completeDays.length / requiredDays) * 100, 100);
    
    const updatedZenVeg = {
      ...zenVeg,
      progress: progress,
      objectives: {
        hydrationDays: hydrationTracker,
        restEnergyDays: restEnergyTracker,
        activityDays: activityTracker,
        sunDays: sunTracker,
        requiredDays: requiredDays
      }
    };
    
    if (completeDays.length >= requiredDays && !zenVeg.completed) {
      updatedZenVeg.completed = true;
      
      localStorage.setItem(`badge_zenVeg_${user.id}`, 'true');
      sessionStorage.setItem(`badge_zenVeg_${user.id}`, 'true');
      
      updateUserPoints(zenVeg.points || 30);
      
      setActiveChallenge(updatedZenVeg);
      setZenCompletionDialogOpen(true);
    }
    
    setZenVeg(updatedZenVeg);
    localStorage.setItem(`challenge_zenVeg_${user.id}`, JSON.stringify(updatedZenVeg));
    sessionStorage.setItem(`challenge_zenVeg_${user.id}`, JSON.stringify(updatedZenVeg));
  }, [user, zenVeg.startDate]);

  const renderIcon = (iconName: string, className: string) => {
    switch (iconName) {
      case "Clock":
        return <Clock className={className} />;
      case "Award":
        return <Award className={className} />;
      case "Trophy":
        return <Trophy className={className} />;
      case "Calendar":
        return <Calendar className={className} />;
      case "Target":
        return <Target className={className} />;
      case "MenuSquare":
        return <MenuSquare className={className} />;
      case "Compass":
        return <Compass className={className} />;
      case "Medal":
        return <Medal className={className} />;
      case "Star":
        return <Star className={className} />;
      case "Sun":
        return <Sun className={className} />;
      case "Droplet":
        return <Droplet className={className} />;
      case "Moon":
        return <Moon className={className} />;
      case "Activity":
        return <Activity className={className} />;
      case "Energy":
        return <Zap className={className} />;
      case "Users":
        return <Users className={className} />;
      default:
        return <Award className={className} />;
    }
  };

  const getRemainingDays = (challenge: Challenge) => {
    if (!challenge.startDate || !challenge.endDate) return "No iniciado";
    
    const today = new Date();
    const endDate = new Date(challenge.endDate);
    const daysLeft = differenceInDays(endDate, today);
    
    return daysLeft > 0 ? `${daysLeft} días restantes` : "Finaliza hoy";
  };

  const shouldShowExplorer = showCompletedChallenges || !explorer.completed;
  const shouldShowZenVeg = showCompletedChallenges || !zenVeg.completed;
  const shouldShowSocial = showCompletedChallenges || !social.completed;

  return (
    <Layout>
      <div className="py-12 px-4 bg-gradient-to-b from-white to-green-50">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "tween" as const }}
            className="flex flex-col gap-2 mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-vegana-primary">
              Retos <span className="text-vegana-secondary">Veganos</span>
            </h1>
            
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowCompletedChallenges(!showCompletedChallenges)}
                className="mt-2 text-sm"
              >
                {showCompletedChallenges ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Ocultar retos completados
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mostrar retos completados
                  </>
                )}
              </Button>
            </div>
            
            <Collapsible open={isLisaBoxOpen} onOpenChange={setIsLisaBoxOpen} className="mt-4">
              <div className="bg-white/90 backdrop-blur-md border border-purple-100 rounded-xl shadow-md p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 h-full w-1/2 bg-gradient-to-r from-purple-100/20 to-transparent z-0"></div>
                <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-purple-200/20 to-transparent z-0"></div>
                
                <div className="relative z-10 flex flex-col items-center sm:items-start gap-4">
                  <Avatar 
                    className="h-14 w-14 border-2 border-purple-300 shadow-lg inline-block bg-white cursor-pointer" 
                    onClick={handleLisaAvatarClick}
                  >
                    <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                    <AvatarFallback>L</AvatarFallback>
                  </Avatar>
                  
                  <CollapsibleContent className="w-full">
                    <p className="text-gray-600 text-center sm:text-left text-sm leading-tight px-1">
                      Aquí te ayudaré en tu camino veg, paso a paso. Completa los retos que te propongo para potenciar tu vitalidad <Heart className="inline-block h-4 w-4 text-vegana-primary" />
                    </p>
                  </CollapsibleContent>
                  
                  <CollapsibleTrigger asChild className="absolute top-4 right-4">
                    <button className="p-1 hover:bg-purple-100 rounded-full">
                      {isLisaBoxOpen ? <ChevronUp className="h-4 w-4 text-purple-500" /> : <ChevronDown className="h-4 w-4 text-purple-500" />}
                    </button>
                  </CollapsibleTrigger>
                </div>
              </div>
            </Collapsible>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4, type: "tween" as const }}
            className="mb-10"
          >
            <Card className="border-t-4 border-green-500 shadow-lg overflow-hidden mb-8">
              <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-transparent flex flex-row justify-between items-center">
                <div>
                  <CardTitle className="text-xl">Sistema de Niveles</CardTitle>
                  <CardDescription>
                    Completa retos y acumula puntos para subir de nivel
                  </CardDescription>
                </div>
                
                <Collapsible>
                  <CollapsibleTrigger 
                    onClick={() => setIsLevelTableOpen(!isLevelTableOpen)} 
                    className="p-1 hover:bg-green-100 rounded-full"
                  >
                    {isLevelTableOpen ? 
                      <ChevronUp className="h-5 w-5 text-green-600" /> : 
                      <ChevronDown className="h-5 w-5 text-green-600" />
                    }
                  </CollapsibleTrigger>
                </Collapsible>
              </CardHeader>
              
              <Collapsible open={isLevelTableOpen} className="w-full">
                <CollapsibleContent>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nivel</TableHead>
                            <TableHead>Nombre del nivel</TableHead>
                            <TableHead>Puntos necesarios</TableHead>
                            <TableHead className="hidden md:table-cell">Descripción</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {levelData.map((level) => (
                            <TableRow key={level.level}>
                              <TableCell className="font-medium">{level.level}</TableCell>
                              <TableCell>{level.name}</TableCell>
                              <TableCell>{level.requiredPoints}</TableCell>
                              <TableCell className="hidden md:table-cell">{level.description}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {user && user.profile && (
                      <div className="mt-6 p-4 bg-green-50 rounded-lg">
                        <h3 className="font-medium mb-2 text-vegana-primary">Tu nivel actual</h3>
                        <UserLevel 
                          points={user.profile.points || 0} 
                          refreshKey={userLevelRefreshKey} 
                        />
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {shouldShowExplorer && (
              <Card className="border-t-4 border-purple-500 shadow-lg overflow-hidden mb-8">
                <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-transparent">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-purple-100">
                        <Compass className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">Explorador Vegano</CardTitle>
                        <CardDescription>
                          Reto mensual • 30 días • {explorer.startDate ? getRemainingDays(explorer) : "No iniciado"}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {explorer.completed && (
                        <Badge variant="mint" className="flex items-center gap-1 px-3 py-1.5">
                          <Medal className="h-4 w-4" />
                          <span>Completado • +{explorer.points} puntos</span>
                        </Badge>
                      )}
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Star className="h-4 w-4 mr-1 text-amber-500" />
                        Ganas: 200 puntos Vital
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 pb-2">
                  <p className="text-gray-700 mb-4">Añade 30 recetas diferentes a tu menú en un periodo de 30 días y realiza al menos un cambio en tu diario nutricional cada día durante 30 días.</p>
                  
                  {explorer.startDate && !explorer.completed && (
                    <div className="space-y-4 mt-2 mb-4">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span>Progreso total</span>
                          <span className="font-semibold">{Math.round(explorer.progress || 0)}%</span>
                        </div>
                        <Progress 
                          value={explorer.progress} 
                          className="h-2 bg-purple-100" 
                          indicatorClassName="bg-purple-500"
                        />
                      </div>
                      
                      <div className="space-y-3 mt-4">
                        {'menuRecipesCount' in explorer.objectives && (
                          <>
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <MenuSquare className="h-4 w-4 text-purple-500" />
                                <span>Recetas en menú</span>
                              </div>
                              <span className="font-semibold">
                                {explorer.objectives.menuRecipesCount} / {explorer.objectives.targetRecipes}
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-purple-500" />
                                <span>Días con cambios</span>
                              </div>
                              <span className="font-semibold">
                                {explorer.objectives.dailyChanges ? explorer.objectives.dailyChanges.length : 0} / 30
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0 pb-4">
                  {!explorer.startDate && !explorer.completed ? (
                    <Button 
                      onClick={startExplorerChallenge}
                      className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
                    >
                      Iniciar Reto
                    </Button>
                  ) : explorer.completed ? (
                    <Button 
                      disabled
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-600"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Reto Completado
                    </Button>
                  ) : (
                    <div className="w-full flex flex-col gap-2">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Inicio: {explorer.startDate && format(new Date(explorer.startDate), 'dd/MM/yyyy')}</span>
                        <span>Fin: {explorer.endDate && format(new Date(explorer.endDate), 'dd/MM/yyyy')}</span>
                      </div>
                    </div>
                  )}
                </CardFooter>
              </Card>
            )}

            {shouldShowZenVeg && (
              <Card className="border-t-4 border-amber-500 shadow-lg overflow-hidden mb-8">
                <CardHeader className="pb-3 bg-gradient-to-r from-amber-50 to-transparent">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-amber-100">
                        <Sun className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">Zen Veg🧘‍♀️</CardTitle>
                        <CardDescription>
                          Reto semanal • 7 días • {zenVeg.startDate ? getRemainingDays(zenVeg) : "No iniciado"}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {zenVeg.completed && (
                        <Badge variant="mint" className="flex items-center gap-1 px-3 py-1.5">
                          <Medal className="h-4 w-4" />
                          <span>Completado • +{zenVeg.points} puntos</span>
                        </Badge>
                      )}
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Star className="h-4 w-4 mr-1 text-amber-500" />
                        Ganas: 30 puntos Vital
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 pb-2">
                  <p className="text-gray-700 mb-4">Durante 7 días seguidos registra tu hidratación, nivel de descanso y energía, actividad física y el sol que has tomado.</p>
                  
                  {zenVeg.startDate && !zenVeg.completed && (
                    <div className="space-y-4 mt-2 mb-4">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span>Progreso total</span>
                          <span className="font-semibold">{Math.round(zenVeg.progress || 0)}%</span>
                        </div>
                        <Progress 
                          value={zenVeg.progress} 
                          className="h-2 bg-amber-100" 
                          indicatorClassName="bg-amber-500"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        {'hydrationDays' in zenVeg.objectives && (
                          <>
                            <div className="flex items-center gap-2">
                              <Droplet className="h-4 w-4 text-blue-500" />
                              <span className="text-sm">Hidratación: {zenVeg.objectives.hydrationDays.length}/7</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Moon className="h-4 w-4 text-purple-500" />
                              <span className="text-sm">Descanso: {zenVeg.objectives.restEnergyDays.length}/7</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Activity className="h-4 w-4 text-green-500" />
                              <span className="text-sm">Actividad: {zenVeg.objectives.activityDays.length}/7</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Sun className="h-4 w-4 text-amber-500" />
                              <span className="text-sm">Sol: {zenVeg.objectives.sunDays.length}/7</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0 pb-4">
                  {!zenVeg.startDate && !zenVeg.completed ? (
                    <Button 
                      onClick={startZenVegChallenge}
                      className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
                    >
                      Iniciar Reto
                    </Button>
                  ) : zenVeg.completed ? (
                    <Button 
                      disabled
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-600"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Reto Completado
                    </Button>
                  ) : (
                    <div className="w-full flex flex-col gap-2">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Inicio: {zenVeg.startDate && format(new Date(zenVeg.startDate), 'dd/MM/yyyy')}</span>
                        <span>Fin: {zenVeg.endDate && format(new Date(zenVeg.endDate), 'dd/MM/yyyy')}</span>
                      </div>
                    </div>
                  )}
                </CardFooter>
              </Card>
            )}

            {shouldShowSocial && (
              <Card className="border-t-4 border-yellow-500 shadow-lg overflow-hidden mb-8">
                <CardHeader className="pb-3 bg-gradient-to-r from-yellow-50 to-transparent">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-full bg-yellow-100">
                        <Users className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">5 Social</CardTitle>
                        <CardDescription>
                          Sin límite de tiempo • {social.startDate ? "En curso" : "No iniciado"}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {social.completed && (
                        <Badge variant="mint" className="flex items-center gap-1 px-3 py-1.5">
                          <Medal className="h-4 w-4" />
                          <span>Completado • +{social.points} puntos</span>
                        </Badge>
                      )}
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Star className="h-4 w-4 mr-1 text-amber-500" />
                        Ganas: 100 puntos Vital
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 pb-2">
                  <p className="text-gray-700 mb-4">Da 5 likes a recetas, escribe 5 comentarios y guarda 5 recetas en favoritos.</p>
                  
                  {social.startDate && !social.completed && (
                    <div className="space-y-4 mt-2 mb-4">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span>Progreso total</span>
                          <span className="font-semibold">
                            {social.objectives && 'likes' in social.objectives 
                              ? Math.round(((
                                  Math.min(social.objectives.likes, 5) + 
                                  Math.min(social.objectives.comments, 5) + 
                                  Math.min(social.objectives.favorites, 5)
                                ) / 15) * 100) 
                              : 0}%
                          </span>
                        </div>
                        <Progress 
                          value={
                            social.objectives && 'likes' in social.objectives 
                              ? ((
                                  Math.min(social.objectives.likes, 5) + 
                                  Math.min(social.objectives.comments, 5) + 
                                  Math.min(social.objectives.favorites, 5)
                                ) / 15) * 100 
                              : 0
                          } 
                          className="h-2 bg-yellow-100" 
                          indicatorClassName="bg-yellow-500"
                        />
                      </div>
                      
                      <div className="space-y-3 mt-4">
                        {'likes' in social.objectives && (
                          <>
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Heart className="h-4 w-4 text-red-500" />
                                  <span>Likes a recetas</span>
                                </div>
                                <span className="font-semibold">
                                  {social.objectives.likes} / 5
                                </span>
                              </div>
                              <Progress 
                                value={(Math.min(social.objectives.likes, 5) / 5) * 100} 
                                className="h-1.5 bg-red-100" 
                                indicatorClassName="bg-red-500"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <MessageSquare className="h-4 w-4 text-blue-500" />
                                  <span>Comentarios</span>
                                </div>
                                <span className="font-semibold">
                                  {social.objectives.comments} / 5
                                </span>
                              </div>
                              <Progress 
                                value={(Math.min(social.objectives.comments, 5) / 5) * 100} 
                                className="h-1.5 bg-blue-100" 
                                indicatorClassName="bg-blue-500"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Bookmark className="h-4 w-4 text-green-500" />
                                  <span>Recetas favoritas</span>
                                </div>
                                <span className="font-semibold">
                                  {social.objectives.favorites} / 5
                                </span>
                              </div>
                              <Progress 
                                value={(Math.min(social.objectives.favorites, 5) / 5) * 100} 
                                className="h-1.5 bg-green-100" 
                                indicatorClassName="bg-green-500"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0 pb-4">
                  {!social.startDate && !social.completed ? (
                    <Button 
                      onClick={startSocialChallenge}
                      className="w-full sm:w-auto bg-vegana-secondary hover:bg-vegana-secondary/80 text-white"
                    >
                      Iniciar Reto
                    </Button>
                  ) : social.completed ? (
                    <Button 
                      disabled
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-600 cursor-not-allowed"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Reto Completado
                    </Button>
                  ) : (
                    <div className="w-full flex flex-col gap-2">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Inicio: {social.startDate && format(new Date(social.startDate), 'dd/MM/yyyy')}</span>
                        <span>Sin fecha límite</span>
                      </div>
                    </div>
                  )}
                </CardFooter>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
      
      <Dialog open={completionDialogOpen} onOpenChange={(open) => {
        setCompletionDialogOpen(open);
        if (!open && user?.id) {
          localStorage.setItem(`challenge_explorer_${user?.id}_completion_shown`, 'true');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¡Reto Explorador Completado!</DialogTitle>
            <DialogDescription>
              Has añadido 30 recetas a tu menú y registrado cambios en tu diario durante 30 días. ¡Felicidades!
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4">
            <div className="text-center mb-4">
              <Compass className="h-16 w-16 text-purple-600 mx-auto mb-2" />
              <p className="font-semibold text-lg text-purple-700">+{explorer.points} puntos</p>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 py-1 px-3">
              <Trophy className="h-4 w-4 mr-2" />
              <span>Insignia desbloqueada: Explorador Vegano</span>
            </Badge>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setCompletionDialogOpen(false)}>
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={zenCompletionDialogOpen} onOpenChange={(open) => {
        setZenCompletionDialogOpen(open);
        if (!open && user?.id) {
          localStorage.setItem(`challenge_zenVeg_${user?.id}_completion_shown`, 'true');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¡Reto Zen Veg Completado!</DialogTitle>
            <DialogDescription>
              Has completado los 7 días de registro en tu diario nutricional. ¡Tu cuerpo te lo agradece!
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4">
            <div className="text-center mb-4">
              <Sun className="h-16 w-16 text-amber-500 mx-auto mb-2" />
              <p className="font-semibold text-lg text-amber-700">+{zenVeg.points} puntos</p>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 py-1 px-3">
              <Trophy className="h-4 w-4 mr-2" />
              <span>Insignia desbloqueada: Zen Veg</span>
            </Badge>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setZenCompletionDialogOpen(false)}>
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={socialCompletionDialogOpen} onOpenChange={(open) => {
        setSocialCompletionDialogOpen(open);
        if (!open && user?.id) {
          localStorage.setItem(`challenge_social_${user?.id}_completion_shown`, 'true');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¡Reto 5 Social Completado!</DialogTitle>
            <DialogDescription>
              Has completado 5 likes, 5 comentarios y 5 favoritos. ¡Eres parte activa de la comunidad!
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4">
            <div className="text-center mb-4">
              <Users className="h-16 w-16 text-yellow-500 mx-auto mb-2" />
              <p className="font-semibold text-lg text-yellow-700">+{social.points} puntos</p>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 py-1 px-3">
              <Trophy className="h-4 w-4 mr-2" />
              <span>Insignia desbloqueada: 5 Social</span>
            </Badge>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setSocialCompletionDialogOpen(false)}>
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Challenges;
